<template>
  <div class="settings--content">
    <div class="pre-chat--title">
      {{ $t('INBOX_MGMT.PRE_CHAT_FORM.DESCRIPTION') }}
    </div>
    <form @submit.prevent="updateInbox">
      <label class="medium-3 columns">
        {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.LABEL') }}
        <select v-model="preChatFormEnabled">
          <option :value="true">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.ENABLED') }}
          </option>
          <option :value="false">
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.DISABLED') }}
          </option>
        </select>
      </label>
      <div v-if="preChatFormEnabled">
        <label class="columns medium-8  large-8">
          <label>
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.LABEL') }}
          </label>
          <woot-message-editor
            v-model="preChatMessage"
            class="message-editor"
            :placeholder="
              $t('INBOX_MGMT.PRE_CHAT_FORM.PRE_CHAT_MESSAGE.PLACEHOLDER')
            "
          />
        </label>
        <div class="columns medium-8 large-8 privacy-policy-settings">
          <label>
            {{ $t('INBOX_MGMT.PRE_CHAT_FORM.PRIVACY_POLICY.ENABLE.LABEL') }}
            <select
              v-model="privacyPolicyEnabled"
              class="privacy-policy-select"
            >
              <option :value="true">
                {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.PRE_CHAT_FORM.ENABLE.OPTIONS.DISABLED') }}
              </option>
            </select>
          </label>
          <template v-if="privacyPolicyEnabled">
            <label>
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.PRIVACY_POLICY.MESSAGE.LABEL') }}
              <textarea
                v-model.trim="privacyPolicyMessage"
                rows="3"
                :placeholder="
                  $t(
                    'INBOX_MGMT.PRE_CHAT_FORM.PRIVACY_POLICY.MESSAGE.PLACEHOLDER'
                  )
                "
              />
            </label>
            <label :class="{ error: privacyPolicyUrlError }">
              {{ $t('INBOX_MGMT.PRE_CHAT_FORM.PRIVACY_POLICY.URL.LABEL') }}
              <input
                v-model.trim="privacyPolicyUrl"
                type="text"
                inputmode="url"
                :placeholder="
                  $t('INBOX_MGMT.PRE_CHAT_FORM.PRIVACY_POLICY.URL.PLACEHOLDER')
                "
                @blur="showPrivacyPolicyUrlError = true"
              />
              <span v-if="privacyPolicyUrlError" class="message">
                {{ $t('INBOX_MGMT.PRE_CHAT_FORM.PRIVACY_POLICY.URL.ERROR') }}
              </span>
            </label>
          </template>
        </div>
        <div class="columns medium-8  large-8 pre-chat-fields">
          <label>{{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS') }}</label>
          <table class="table table-striped w-full">
            <thead class="thead-dark">
              <tr>
                <th scope="col" />
                <th scope="col" />
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.KEY') }}
                </th>
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.TYPE') }}
                </th>
                <th scope="col">
                  {{
                    $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.REQUIRED')
                  }}
                </th>
                <th scope="col">
                  {{ $t('INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.LABEL') }}
                </th>
                <th scope="col">
                  {{
                    $t(
                      'INBOX_MGMT.PRE_CHAT_FORM.SET_FIELDS_HEADER.PLACE_HOLDER'
                    )
                  }}
                </th>
              </tr>
            </thead>
            <pre-chat-fields
              :pre-chat-fields="preChatFields"
              @update="handlePreChatFieldOptions"
              @drag-end="changePreChatFieldFieldsOrder"
            />
          </table>
        </div>
      </div>
      <woot-submit-button
        class="submit-button"
        :button-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE_PRE_CHAT_FORM_SETTINGS')
        "
        :loading="uiFlags.isUpdating"
      />
    </form>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import PreChatFields from './PreChatFields.vue';
import { getPreChatFields, standardFieldKeys } from 'dashboard/helper/preChat';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
export default {
  components: {
    PreChatFields,
    WootMessageEditor,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      preChatFormEnabled: false,
      preChatMessage: '',
      preChatFields: [],
      privacyPolicyEnabled: false,
      privacyPolicyMessage: '',
      privacyPolicyUrl: '',
      showPrivacyPolicyUrlError: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      customAttributes: 'attributes/getAttributes',
    }),
    isPrivacyPolicyUrlValid() {
      return /^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(this.privacyPolicyUrl);
    },
    privacyPolicyUrlError() {
      return (
        this.privacyPolicyEnabled &&
        this.showPrivacyPolicyUrlError &&
        !this.isPrivacyPolicyUrlValid
      );
    },
    preChatFieldOptions() {
      const { pre_chat_form_options: preChatFormOptions } = this.inbox;
      return getPreChatFields({
        preChatFormOptions,
        customAttributes: this.customAttributes,
      });
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const { pre_chat_form_enabled: preChatFormEnabled } = this.inbox;
      this.preChatFormEnabled = preChatFormEnabled;
      const {
        pre_chat_message: preChatMessage,
        pre_chat_fields: preChatFields,
      } = this.preChatFieldOptions || {};
      this.preChatMessage = preChatMessage;
      this.preChatFields = preChatFields;

      const options = this.inbox.pre_chat_form_options || {};
      this.privacyPolicyEnabled = !!options.privacy_policy_enabled;
      this.privacyPolicyMessage = options.privacy_policy_message || '';
      this.privacyPolicyUrl = options.privacy_policy_url || '';
      this.showPrivacyPolicyUrlError = false;
    },
    isFieldEditable(item) {
      return !!standardFieldKeys[item.name] || !item.enabled;
    },
    handlePreChatFieldOptions(event, type, item) {
      this.preChatFields.forEach((field, index) => {
        if (field.name === item.name) {
          this.preChatFields[index][type] = !item[type];
        }
      });
    },

    changePreChatFieldFieldsOrder(updatedPreChatFieldOptions) {
      this.preChatFields = updatedPreChatFieldOptions;
    },

    async updateInbox() {
      if (
        this.preChatFormEnabled &&
        this.privacyPolicyEnabled &&
        !this.isPrivacyPolicyUrlValid
      ) {
        this.showPrivacyPolicyUrlError = true;
        return;
      }
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            pre_chat_form_enabled: this.preChatFormEnabled,
            pre_chat_form_options: {
              pre_chat_message: this.preChatMessage,
              pre_chat_fields: this.preChatFields,
              privacy_policy_enabled: this.privacyPolicyEnabled,
              privacy_policy_message: this.privacyPolicyMessage,
              privacy_policy_url: this.privacyPolicyUrl,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(
          error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>
<style scoped lang="scss">
.settings--content {
  font-size: var(--font-size-default);
}
.pre-chat--title {
  margin: var(--space-medium) 0 var(--space-slab);
}

.submit-button {
  margin-top: var(--space-normal);
}
.privacy-policy-settings {
  margin-top: var(--space-normal);

  .privacy-policy-select {
    max-width: 24rem;
  }
}
.pre-chat-fields {
  margin-top: var(--space-normal);
}
</style>
